import axios from "axios";
import { toSnakeCase, toCamelCase } from "./data-format";
import { getToken, removeToken, setToken, getUserInfo, removeAll } from "@/utils/auth";
import { refresh } from "@/api/security";
import { Message } from "element-ui";
import router from "@/router";
import store from "@/store";
const http = axios.create({
    baseURL: process.env.VUE_APP_BASE_API,
    timeout: 30000,
});

// console.log(process.env.ENV)

http.interceptors.request.use(
    (config) => {
        const token = getToken();
        if (token != undefined) {
            config.headers.common["Authorization"] = `Bearer ${token}`;
        }
        config.headers.common["accept-language"] = store.getters.language;
        if (!config.unformat) {
            config.params = toSnakeCase(config.params);
            config.data = toSnakeCase(config.data);
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// 1. 全局维护一个“正在刷新 Token”的 Promise，防止并发请求重复刷新
let refreshingPromise = null;

http.interceptors.response.use(
    (response) => {
        // 2. 更稳健地判断 JSON 类型
        const contentType = response.headers["content-type"];
        if (contentType && contentType.includes("application/json")) {
            response.data = toCamelCase(response.data);
        }
        return response;
    },
    async (error) => {
        // 3. 首先处理网络错误或无响应的情况
        if (!error.response) {
            console.error("Network Error or No Response:", error);
            Message.error("網絡異常，請檢查連接后重試");
            return Promise.reject(error);
        }

        const { status, data } = error.response;

        if (status === 401) {
            // 4. 处理 Token 过期
            // 如果已有刷新请求，则等待其完成，避免并发
            if (!refreshingPromise) {
                const refreshToken = getUserInfo("refreshToken");
                // 检查 refreshToken 是否存在
                if (!refreshToken) {
                    goToLogin(); // 封装的跳转登录函数
                    return Promise.reject(error);
                }

                const d = { refreshToken };
                // 将刷新请求的 Promise 保存起来
                refreshingPromise = refresh(d)
                    .then((v) => {
                        // 刷新成功，更新本地 Token
                        setToken(v.data.accessToken);
                        return v.data.accessToken; // 将新 Token 传递下去
                    })
                    .catch((refreshError) => {
                        // 刷新 Token 本身也失败了，说明 refreshToken 无效
                        console.error("Token refresh failed:", refreshError);
                        goToLogin();
                        return Promise.reject(refreshError);
                    })
                    .finally(() => {
                        // 无论成功失败，都重置刷新状态
                        refreshingPromise = null;
                    });
            }

            try {
                // 5. 等待刷新 Token 完成，然后重试原请求
                const newToken = await refreshingPromise;
                const config = error.config;
                // 更新原请求的 Authorization 头
                config.headers.Authorization = `Bearer ${newToken}`;
                // 重试请求，并返回新的 Promise
                return http(config);
            } catch (refreshError) {
                // 如果刷新失败，这里会捕获到，直接 reject
                return Promise.reject(refreshError);
            }
        } else if (status === 500) {
            // 6. 更通用的 500 错误处理
            console.error("Server Error (500):", data);
            const errorMsg = data?.message || "服務器内部錯誤，請稍後重試。";
            Message.error(errorMsg);
        } else if (
            // 7. 使用 error_code 进行判断，更可靠
            data?.error_code === "INVALID_USER_ACCOUNT" ||
            data?.error_code === "INVALID_PARENT_PASSWORD" ||
            data?.error_code === "INVALID_STUDENT_PASSWORD" ||
            data?.error_code === "SUBSCRIPTION_EXPIRED_ERROR" ||
            data?.error_code === "NO_SUBSCRIPTION_ERROR" ||
            data?.error_code === "SUBSCRIPTION_NOT_START_ERROR" ||
            data?.error_code === "AUTH_ERROR"
        ) {
            console.warn("Business Logic Error:", data);
            // 对于这些业务错误，可以给用户更具体的提示
            if(data.error_message != null && data.error_message != ''){
                Message.warning(data.error_message);
            }
        } else {
            // 8. 其他所有未预料到的错误
            console.error(`Unhandled Error (${status}):`, data);
            const errorMsg = data?.error_message || data?.message;
            if(errorMsg != null && errorMsg != ''){
                Message.error(errorMsg);
            }
        }

        // 9. 最后，将错误继续向下传递
        return Promise.reject(error);
    }
);

// 封装一个通用的跳转登录函数
function goToLogin() {
    removeToken();
    removeAll();
    window.sessionStorage.clear();
    router.push({ path: "/login" });
}
export default http;
